import React, { useEffect, useState } from 'react';
import { X, CheckCircle2, TrendingUp, AlertCircle, Award, Zap, ChevronDown, ChevronUp } from 'lucide-react';
import {
  getMotivationalQuote,
  getEncouragementMessage,
} from '@/services/motivationalQuotesService';
import { examService } from '@/services/examService';
import type { ExamMCQQuestion, UserAnswerIndex } from '@/types/exam';

interface ExamResultsModalProps {
  exam: {
    roadmap_id: string;
    roadmap_name: string;
    roadmap_difficulty: string;
  };
  result: {
    passed: boolean;
    score_percentage: number;
    correct_answers: number;
    total_questions: number;
    examAttemptId?: string;
  };
  timeSpent: number;
  onRetry: () => void;
  onGetCertificate: (examAttemptId: string) => void;
  onClose: () => void;
  userId: string;
  questions?: ExamMCQQuestion[];
  userAnswers?: UserAnswerIndex[];
}

const ExamResultsModal: React.FC<ExamResultsModalProps> = ({
  exam,
  result,
  timeSpent,
  onRetry,
  onGetCertificate,
  onClose,
  userId,
  questions = [],
  userAnswers = [],
}) => {
  const [attemptInfo, setAttemptInfo] = useState<{
    total_attempts: number;
    remaining_attempts: number;
    can_attempt: boolean;
  } | null>(null);
  const [loading, setLoading] = useState(true);
  const [expandedAnswers, setExpandedAnswers] = useState<Set<number>>(new Set());

  const quote = getMotivationalQuote(result.score_percentage);
  const encouragement = getEncouragementMessage(
    result.score_percentage,
    result.correct_answers,
    result.total_questions,
    attemptInfo?.total_attempts || 1
  );

  useEffect(() => {
    const fetchAttemptInfo = async () => {
      try {
        const info = await examService.getExamAttemptCount(userId, exam.roadmap_id);
        setAttemptInfo(info);
      } catch (err) {
        console.error('Error fetching attempt info:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchAttemptInfo();
  }, [userId, exam.roadmap_id]);

  const formatTime = (seconds: number) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    if (hours > 0) {
      return `${hours}:${minutes.toString().padStart(2, '0')}:${secs
        .toString()
        .padStart(2, '0')}`;
    }
    return `${minutes}:${secs.toString().padStart(2, '0')}`;
  };

  const scoreColor = result.passed
    ? 'from-green-500 to-emerald-500'
    : result.score_percentage >= 60
      ? 'from-orange-500 to-amber-500'
      : 'from-red-500 to-rose-500';

  const scorePercentageDisplay = `${result.score_percentage}%`;
  const accuracy = Math.round((result.correct_answers / result.total_questions) * 100);

  // Get list of wrong answers
  const wrongAnswers = questions
    .map((question, index) => ({
      index,
      question,
      userAnswer: userAnswers[index],
      isWrong: userAnswers[index] !== null && userAnswers[index] !== question.correctAnswer,
    }))
    .filter((item) => item.isWrong);

  const toggleExpandedAnswer = (index: number) => {
    const newExpanded = new Set(expandedAnswers);
    if (newExpanded.has(index)) {
      newExpanded.delete(index);
    } else {
      newExpanded.add(index);
    }
    setExpandedAnswers(newExpanded);
  };

  return (
    <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="w-full max-w-2xl max-h-[90vh] overflow-y-auto bg-gradient-to-b from-slate-900 via-slate-900 to-slate-950 rounded-2xl shadow-2xl border border-white/5">
        {/* Header with close button and gradient */}
        <div className={`relative bg-gradient-to-r ${scoreColor} p-1`}>
          <div className="absolute inset-0 opacity-20 bg-grid-white/5" />
          <div className="relative bg-slate-900 rounded-[14px] p-8 flex items-start justify-between gap-4">
            <div className="flex-1">
              <div className="flex items-center gap-3 mb-3">
                {result.passed ? (
                  <div className="p-2 bg-green-500/20 rounded-lg animate-pulse">
                    <CheckCircle2 className="w-8 h-8 text-green-400" />
                  </div>
                ) : (
                  <div className="p-2 bg-orange-500/20 rounded-lg">
                    <TrendingUp className="w-8 h-8 text-orange-400" />
                  </div>
                )}
                <div>
                  <h2 className="text-2xl font-bold text-white">
                    {result.passed ? '🎉 Exam Passed!' : '📚 Keep Learning!'}
                  </h2>
                  <p className="text-sm text-slate-400 mt-1">
                    {exam.roadmap_name} • <span className="capitalize">{exam.roadmap_difficulty}</span>
                  </p>
                </div>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-white/10 rounded-lg transition-colors flex-shrink-0 text-slate-400 hover:text-white"
              aria-label="Close"
            >
              <X className="w-6 h-6" />
            </button>
          </div>
        </div>

        {/* Main Content */}
        <div className="p-8 space-y-6">
          {/* Score Cards Grid */}
          <div className="grid grid-cols-3 gap-4">
            {/* Score Card */}
            <div className="group relative bg-gradient-to-br from-blue-500/10 to-blue-600/5 border border-blue-500/20 rounded-xl p-6 overflow-hidden hover:border-blue-500/40 transition-all">
              <div className="absolute inset-0 bg-gradient-to-br from-blue-500/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
              <div className="relative z-10">
                <p className="text-xs font-semibold text-blue-300 uppercase tracking-wider mb-3">Score</p>
                <p className={`text-4xl font-black bg-gradient-to-r ${scoreColor} bg-clip-text text-transparent`}>
                  {scorePercentageDisplay}
                </p>
                <div className="mt-3 w-full h-1.5 bg-white/5 rounded-full overflow-hidden">
                  <div
                    className={`h-full bg-gradient-to-r ${scoreColor} rounded-full transition-all duration-700`}
                    style={{ width: `${result.score_percentage}%` }}
                  />
                </div>
              </div>
            </div>

            {/* Correct Answers Card */}
            <div className="group relative bg-gradient-to-br from-green-500/10 to-green-600/5 border border-green-500/20 rounded-xl p-6 overflow-hidden hover:border-green-500/40 transition-all">
              <div className="absolute inset-0 bg-gradient-to-br from-green-500/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
              <div className="relative z-10">
                <p className="text-xs font-semibold text-green-300 uppercase tracking-wider mb-3">Correct</p>
                <p className="text-4xl font-black text-green-400">
                  {result.correct_answers}/{result.total_questions}
                </p>
                <p className="text-xs text-green-300/60 mt-3">{accuracy}% Accuracy</p>
              </div>
            </div>

            {/* Time Spent Card */}
            <div className="group relative bg-gradient-to-br from-purple-500/10 to-purple-600/5 border border-purple-500/20 rounded-xl p-6 overflow-hidden hover:border-purple-500/40 transition-all">
              <div className="absolute inset-0 bg-gradient-to-br from-purple-500/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
              <div className="relative z-10">
                <p className="text-xs font-semibold text-purple-300 uppercase tracking-wider mb-3">Time</p>
                <p className="text-4xl font-black text-purple-400">{formatTime(timeSpent)}</p>
                <p className="text-xs text-purple-300/60 mt-3">Time Spent</p>
              </div>
            </div>
          </div>

          {/* Motivational Quote */}
          <div className="relative bg-gradient-to-r from-rose-500/10 to-pink-500/10 border border-rose-500/30 rounded-xl p-6 overflow-hidden">
            <div className="absolute top-0 left-0 w-1 h-full bg-gradient-to-b from-rose-500 to-pink-500" />
            <div className="relative z-10">
              <p className="text-lg font-semibold text-white italic leading-relaxed">
                {quote.quote}
              </p>
            </div>
          </div>

          {/* Encouragement Message */}
          <div className="bg-slate-800/50 border border-slate-700/50 rounded-lg p-4">
            <p className="text-center text-slate-200 font-medium text-sm">
              {encouragement}
            </p>
          </div>

          {/* Wrong Answers Review Section */}
          {wrongAnswers.length > 0 && (
            <div className="bg-rose-500/5 border border-rose-500/30 rounded-lg overflow-hidden">
              <div className="p-4 bg-rose-500/10 border-b border-rose-500/20">
                <h3 className="text-lg font-semibold text-rose-300">📝 Review Wrong Answers</h3>
                <p className="text-xs text-rose-200/70 mt-1">
                  {wrongAnswers.length} {wrongAnswers.length === 1 ? 'answer' : 'answers'} to review
                </p>
              </div>

              <div className="divide-y divide-rose-500/20">
                {wrongAnswers.map((item) => (
                  <div key={item.index} className="p-4">
                    <button
                      onClick={() => toggleExpandedAnswer(item.index)}
                      className="w-full text-left flex items-start justify-between gap-3 p-3 hover:bg-white/5 rounded-lg transition-colors"
                    >
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start gap-2 mb-2">
                          <span className="text-xs font-semibold text-slate-400 flex-shrink-0">
                            Q{item.index + 1}
                          </span>
                          <p className="text-sm font-medium text-rose-200 line-clamp-2">
                            {item.question.question}
                          </p>
                        </div>
                        <div className="flex items-center gap-2 text-xs">
                          <span className="px-2 py-1 bg-red-500/20 text-red-300 rounded">
                            Your answer: {String.fromCharCode(65 + (item.userAnswer ?? 0))}
                          </span>
                          <span className="px-2 py-1 bg-green-500/20 text-green-300 rounded">
                            Correct: {String.fromCharCode(65 + item.question.correctAnswer)}
                          </span>
                        </div>
                      </div>
                      <div className="flex-shrink-0 text-slate-400">
                        {expandedAnswers.has(item.index) ? (
                          <ChevronUp className="w-5 h-5" />
                        ) : (
                          <ChevronDown className="w-5 h-5" />
                        )}
                      </div>
                    </button>

                    {expandedAnswers.has(item.index) && (
                      <div className="mt-3 ml-3 pl-3 border-l border-rose-500/30 space-y-3">
                        <div>
                          <p className="text-xs font-semibold text-slate-400 mb-2 uppercase">
                            Options
                          </p>
                          <div className="space-y-2">
                            {item.question.options.map((option, optIdx) => {
                              const isCorrect = optIdx === item.question.correctAnswer;
                              const isSelected = optIdx === item.userAnswer;
                              return (
                                <div
                                  key={optIdx}
                                  className={`p-2 rounded text-sm ${
                                    isCorrect
                                      ? 'bg-green-500/20 text-green-300 border border-green-500/30'
                                      : isSelected
                                        ? 'bg-red-500/20 text-red-300 border border-red-500/30'
                                        : 'bg-slate-700/30 text-slate-300 border border-slate-600/30'
                                  }`}
                                >
                                  <span className="font-semibold mr-2">
                                    {String.fromCharCode(65 + optIdx)}.
                                  </span>
                                  {option}
                                  {isCorrect && ' ✓'}
                                  {isSelected && !isCorrect && ' ✗'}
                                </div>
                              );
                            })}
                          </div>
                        </div>

                        {item.question.explanation && (
                          <div>
                            <p className="text-xs font-semibold text-slate-400 mb-2 uppercase">
                              Explanation
                            </p>
                            <p className="text-sm text-slate-300 bg-slate-800/40 p-3 rounded border border-slate-700/50">
                              {item.question.explanation}
                            </p>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Attempt Info */}
          {!loading && attemptInfo && (
            <div className="flex items-start gap-3 p-4 bg-blue-500/10 border border-blue-500/30 rounded-lg">
              <AlertCircle className="w-5 h-5 text-blue-400 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-blue-300 mb-1">
                  {attemptInfo.remaining_attempts === 1
                    ? `⚠️ This is your LAST attempt`
                    : attemptInfo.remaining_attempts === 0
                      ? `❌ No attempts remaining`
                      : `Attempts: ${attemptInfo.remaining_attempts} of ${attemptInfo.total_attempts + attemptInfo.remaining_attempts}`}
                </p>
                <p className="text-xs text-blue-200/70">
                  {attemptInfo.remaining_attempts === 1
                    ? 'Review the material carefully before your final attempt'
                    : attemptInfo.remaining_attempts === 0
                      ? 'Complete the roadmap again to unlock new attempts'
                      : 'Each attempt builds your understanding'}
                </p>
              </div>
            </div>
          )}

          {/* Conditional Messages */}
          {!result.passed && (
            <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <Zap className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-amber-300 mb-1">💡 Pro Tip</p>
                  <p className="text-xs text-amber-200/70">
                    Focus on the topics you struggled with. Review the roadmap content and come back stronger!
                  </p>
                </div>
              </div>
            </div>
          )}

          {result.passed && (
            <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <Award className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-green-300 mb-1">🏆 Achievement Unlocked!</p>
                  <p className="text-xs text-green-200/70">
                    Your certificate is ready to download and share. Prove your expertise to the world!
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Action Buttons */}
        <div className="sticky bottom-0 bg-slate-950/80 backdrop-blur border-t border-white/5 px-8 py-4 flex gap-3">
          <button
            onClick={onClose}
            className="flex-1 px-4 py-3 rounded-lg font-semibold text-slate-300 bg-slate-800/50 hover:bg-slate-700/50 border border-slate-700 transition-all duration-200 hover:text-white"
          >
            Close
          </button>

          {!result.passed && attemptInfo && attemptInfo.can_attempt && (
            <button
              onClick={onRetry}
              className="flex-1 px-4 py-3 rounded-lg font-semibold text-white bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600 transition-all duration-200 transform hover:scale-105 active:scale-95 shadow-lg hover:shadow-xl"
            >
              🔄 Retry Exam
            </button>
          )}

          {!result.passed && attemptInfo && !attemptInfo.can_attempt && (
            <button
              disabled
              className="flex-1 px-4 py-3 rounded-lg font-semibold text-slate-500 bg-slate-700/30 cursor-not-allowed opacity-60 border border-slate-700"
            >
              No Attempts Left
            </button>
          )}

          {result.passed && (
            <button
              onClick={() => onGetCertificate(result.examAttemptId || '')}
              className="flex-1 px-4 py-3 rounded-lg font-semibold text-white bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-600 hover:to-emerald-600 transition-all duration-200 transform hover:scale-105 active:scale-95 shadow-lg hover:shadow-xl"
            >
              📜 Download Certificate
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default ExamResultsModal;
